import { motion } from "framer-motion";
import { FaTrophy, FaMedal, FaAward } from "react-icons/fa";
import "./Scoreboard.css";

const Scoreboard = ({ currentScore, playerName, onPlayAgain, onBackToStart }) => {
  // Predefined scores with smaller amounts for new users
  const predefinedScores = [
    { name: "Alice Johnson", score: 1000000, date: "2024-01-15" },
    { name: "Bob Smith", score: 500000, date: "2024-01-10" },
    { name: "Liam Davis", score: 500, date: "2023-12-26" },
    { name: "Maya Patel", score: 300, date: "2023-12-25" },
    { name: "Noah Kim", score: 200, date: "2023-12-24" },
    { name: "Olivia White", score: 100, date: "2023-12-23" },
  ];

  // Get scores from localStorage
  const getStoredScores = () => {
    const stored = localStorage.getItem("quizScores");
    return stored ? JSON.parse(stored) : [];
  };

  // Save current score to localStorage
  const saveScore = () => {
    const storedScores = getStoredScores();
    const newScore = {
      name: playerName,
      score: currentScore,
      date: new Date().toISOString().split('T')[0]
    };
    
    const updatedScores = [...storedScores, newScore]
      .sort((a, b) => b.score - a.score)
      .slice(0, 20); // Keep top 20
    
    localStorage.setItem("quizScores", JSON.stringify(updatedScores));
    return updatedScores;
  };

  // Combine and sort all scores
  const allScores = [...predefinedScores, ...saveScore()]
    .sort((a, b) => b.score - a.score)
    .slice(0, 15); // Show top 15

  const formatScore = (score) => {
    return score.toLocaleString();
  };

  const getRankIcon = (index) => {
    switch (index) {
      case 0: return <FaTrophy className="trophy gold" />;
      case 1: return <FaMedal className="medal silver" />;
      case 2: return <FaAward className="award bronze" />;
      default: return <span className="rank-number">{index + 1}</span>;
    }
  };

  const getCurrentPlayerRank = () => {
    const playerIndex = allScores.findIndex(
      score => score.name === playerName && score.score === currentScore
    );
    return playerIndex !== -1 ? playerIndex + 1 : null;
  };

  return (
    <motion.div 
      className="scoreboard"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.h1 
        className="scoreboard-title"
        initial={{ y: -50 }}
        animate={{ y: 0 }}
        transition={{ delay: 0.2 }}
      >
        🏆 LEADERBOARD 🏆
      </motion.h1>
      
      <motion.div 
        className="current-score"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.4, type: "spring" }}
      >
        <h2>Your Score: ${formatScore(currentScore)}</h2>
        <p>Well done, {playerName}!</p>
        {getCurrentPlayerRank() && (
          <p className="rank-info">You ranked #{getCurrentPlayerRank()} on the leaderboard!</p>
        )}
      </motion.div>

      <motion.div 
        className="scores-list"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        {allScores.map((score, index) => (
          <motion.div
            key={`${score.name}-${score.date}-${index}`}
            className={`score-item ${score.name === playerName && score.score === currentScore ? 'current-player' : ''}`}
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.7 + index * 0.05 }}
          >
            <div className="rank">
              {getRankIcon(index)}
            </div>
            <div className="player-info">
              <span className="player-name">{score.name}</span>
              <span className="player-date">{score.date}</span>
            </div>
            <div className="player-score">${formatScore(score.score)}</div>
          </motion.div>
        ))}
      </motion.div>

      <motion.div 
        className="scoreboard-actions"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <button className="btn-primary" onClick={onPlayAgain}>
          🔄 Play Again
        </button>
        <button className="btn-secondary" onClick={onBackToStart}>
          👤 New Player
        </button>
      </motion.div>
    </motion.div>
  );
};

export default Scoreboard;